// =============================================================================
// src/views/comparador/comparador-modes.ts — Logica dos 3 modos do Comparador.
//
// Proveniencia: assets/unified-app.js Sections.comparador._activeGroupKeys /
//   _applyMode / _applyDiffMode / _applySplitMode / _applyOverlayMode /
//   _updateInsight / _removeDonutMarkers (linhas 1172-1272). Portado FIEL:
//   thresholds e opacidades (diff 0.35 + min(share/100, 0.55); split 0.3 +
//   (share/100)*0.5; overlay base #888780 fillOpacity 0.2), e o texto de insight
//   por modo. NAO alterar numeros/cores — sao paridade visual.
//
// O estado mutavel (currentMode/disabledGroups/donutMarkers) vive em
//   ComparadorModeState, substituindo os campos do objeto-singleton original.
//   O MapController chega por argumento (DI), sem global escondido.
//
// SRP: so a regra dos modos. O SVG/ciclo dos donuts mora em comparador-donut.ts;
//   markup em comparador-markup.ts; binding em index.ts.
// =============================================================================

import type { PathOptions, Marker, LatLngExpression } from 'leaflet';
import type {
  MapController,
  ZonaKey,
  GroupKey,
  ZoneStyleFn,
} from '../../app/types';
import {
  MANAUS_GROUPS,
  MANAUS_ZONE_GROUP_DATA,
  MANAUS_ZONA_DISPLAY_NAME,
} from '../../data';
import {
  buildComparadorDonutSvg,
  addComparadorDonutMarker,
} from './comparador-donut';

/** Modo de visualizacao corrente do Comparador (data-mode dos tabs). */
export type ComparadorMode = 'diff' | 'split' | 'overlay';

/** As 3 chaves de grupo, na ordem do original (['A','B','C']). */
const GROUP_KEYS: readonly GroupKey[] = ['A', 'B', 'C'];

/**
 * Campos de runtime do MapController acessados pelo Comparador que NAO estao no
 * contrato publico (types.ts). Proveniencia: o original le `mapCtrl.map`
 * (exposto no contrato) + `mapCtrl.zoneCentroids` (1222) e zera
 * `MapController.currentZoneStyleFn` no deactivate (1276). Modelados como
 * opcionais para acesso defensivo, sem alargar o contrato compartilhado — mesmo
 * padrao de perfil-regiao/demografica.
 */
interface ComparadorMapRuntimeExtras {
  zoneCentroids?: Partial<Record<ZonaKey, LatLngExpression>>;
  currentZoneStyleFn?: ZoneStyleFn | null;
}

/**
 * Estado encapsulado do Comparador. Substitui os campos mutaveis do
 * Sections.comparador (currentMode/disabledGroups/donutMarkers). Mutavel por
 * design — espelha o comportamento do singleton original.
 */
export interface ComparadorModeState {
  currentMode: ComparadorMode;
  disabledGroups: Partial<Record<GroupKey, boolean>>;
  donutMarkers: Marker[];
}

/** Cria o estado inicial (modo 'diff', nenhum grupo desabilitado, sem donuts). */
export function createComparadorModeState(): ComparadorModeState {
  return { currentMode: 'diff', disabledGroups: {}, donutMarkers: [] };
}

/**
 * Chaves de grupo ativas (nao desabilitadas). Se todas estiverem desabilitadas,
 * cai de volta para as 3 — exatamente como _activeGroupKeys (1172-1176).
 */
export function activeGroupKeys(state: ComparadorModeState): GroupKey[] {
  const keys = GROUP_KEYS.filter((key) => !state.disabledGroups[key]);
  return keys.length ? keys : [...GROUP_KEYS];
}

/** Grupo dominante numa zona entre as chaves ativas (replica o loop de _applyDiffMode). */
function dominantGroupOf(zonaKey: ZonaKey, activeKeys: readonly GroupKey[]): GroupKey {
  const shares = MANAUS_ZONE_GROUP_DATA[zonaKey];
  /* v8 ignore next -- activeGroupKeys nunca retorna vazio; activeKeys[0] sempre definido (?? 'A' defensivo) */
  let dominant = activeKeys[0] ?? 'A';
  activeKeys.forEach((key) => {
    if (shares[key] > shares[dominant]) dominant = key;
  });
  return dominant;
}

/** Estilo de zona do modo "Diferenca": cor do dominante, opacidade pela share. */
function diffZoneStyle(zonaKey: ZonaKey, activeKeys: readonly GroupKey[]): PathOptions {
  const dominant = dominantGroupOf(zonaKey, activeKeys);
  const share = MANAUS_ZONE_GROUP_DATA[zonaKey][dominant];
  return {
    fillColor: MANAUS_GROUPS[dominant].color,
    fillOpacity: 0.35 + Math.min(share / 100, 0.55),
    weight: 0.8,
    color: 'rgba(255,255,255,0.6)',
    opacity: 1,
  };
}

/**
 * Aplica o modo "Diferenca" no mapa (replica _applyDiffMode 1185-1197). Exportado
 * porque o legado chama `_applyDiffMode` DIRETO no activate (1148), sem passar pelo
 * `_applyMode` (que limparia donuts/split antes) — preservar a entrada FIEL.
 */
export function applyDiffMode(state: ComparadorModeState, mapCtrl: MapController): void {
  const activeKeys = activeGroupKeys(state);
  mapCtrl.setZoneStyles((zonaKey) => diffZoneStyle(zonaKey, activeKeys));
}

/** Estilo de uma metade do split (grupo A ou B): opacidade pela share daquele grupo. */
function splitHalfStyle(zonaKey: ZonaKey, group: 'A' | 'B'): PathOptions {
  const share = MANAUS_ZONE_GROUP_DATA[zonaKey][group];
  return {
    fillColor: MANAUS_GROUPS[group].color,
    fillOpacity: 0.3 + (share / 100) * 0.5,
    weight: 0.8,
    color: 'rgba(255,255,255,0.5)',
    opacity: 1,
  };
}

/** Aplica o modo "Lado a lado": split A (esquerda) x B (direita) (_applySplitMode). */
function applySplitMode(mapCtrl: MapController): void {
  mapCtrl.setSplitMode(
    true,
    (zonaKey) => splitHalfStyle(zonaKey, 'A'),
    (zonaKey) => splitHalfStyle(zonaKey, 'B'),
  );
}

/** Estilo cinza-translucido de fundo do modo overlay (_applyOverlayMode 1216-1218). */
function overlayBaseZoneStyle(): PathOptions {
  return {
    fillColor: '#888780',
    fillOpacity: 0.2,
    weight: 0.8,
    color: 'rgba(255,255,255,0.5)',
    opacity: 0.7,
  };
}

/** Cria o donut de uma zona no seu centroide, se houver centroide (corpo do forEach). */
function placeDonutForZone(
  state: ComparadorModeState,
  mapCtrl: MapController,
  zonaKey: ZonaKey,
): void {
  const extras = mapCtrl as MapController & ComparadorMapRuntimeExtras;
  const centroid = extras.zoneCentroids?.[zonaKey];
  if (!centroid || !mapCtrl.map) return;
  const shares = MANAUS_ZONE_GROUP_DATA[zonaKey];
  const activeKeys = activeGroupKeys(state);
  const total = activeKeys.reduce((sum, key) => sum + shares[key], 0);
  /* v8 ignore next 4 -- MANAUS_ZONE_GROUP_DATA tem A/B/C sempre >0; || 0 defensivo CLAUDE.md rule 11 */
  const svg = buildComparadorDonutSvg(
    (shares.A || 0) / total,
    (shares.B || 0) / total,
    (shares.C || 0) / total,
    activeKeys,
  );
  state.donutMarkers.push(addComparadorDonutMarker(mapCtrl.map, centroid, svg));
}

/** Aplica o modo "Sobreposicao": base cinza + donuts por zona (_applyOverlayMode). */
function applyOverlayMode(state: ComparadorModeState, mapCtrl: MapController): void {
  mapCtrl.setZoneStyles(() => overlayBaseZoneStyle());
  (Object.keys(MANAUS_ZONA_DISPLAY_NAME) as ZonaKey[]).forEach((zonaKey) => {
    placeDonutForZone(state, mapCtrl, zonaKey);
  });
}

/** No-op style fn passada a setSplitMode(false) — o controller ignora ao desabilitar. */
function noopSplitStyle(): PathOptions {
  return {};
}

/**
 * Remove todos os donuts e desliga o split (replica _removeDonutMarkers 1268-1272).
 * O contrato exige 3 args em setSplitMode; passamos no-ops pois o controller nao
 * le as styleFns quando enabled === false.
 */
export function removeComparadorDonuts(state: ComparadorModeState, mapCtrl: MapController): void {
  if (mapCtrl.map) {
    state.donutMarkers.forEach((marker) => mapCtrl.map?.removeLayer(marker));
  }
  state.donutMarkers = [];
  mapCtrl.setSplitMode(false, noopSplitStyle, noopSplitStyle);
}

/**
 * Aplica o modo corrente (replica _applyMode 1178-1183): limpa donuts/split antes
 * e despacha para o ramo do modo ativo. Ordem identica ao original.
 */
export function applyComparadorMode(state: ComparadorModeState, mapCtrl: MapController): void {
  removeComparadorDonuts(state, mapCtrl);
  if (state.currentMode === 'diff') applyDiffMode(state, mapCtrl);
  if (state.currentMode === 'split') applySplitMode(mapCtrl);
  if (state.currentMode === 'overlay') applyOverlayMode(state, mapCtrl);
}

/** Texto de insight por modo (replica o objeto textByMode de _updateInsight). */
function comparadorInsightHtml(state: ComparadorModeState): string {
  const active = activeGroupKeys(state)
    .map((key) => MANAUS_GROUPS[key].name)
    .join(', ');
  const textByMode: Record<ComparadorMode, string> = {
    diff:
      '<strong>Direita PL+UB</strong> domina Centro-Sul e Sul, enquanto <strong>Centrão</strong> segura Norte e Leste. Grupos ativos: ' +
      active +
      '.',
    split:
      'Modo lado a lado separa a força territorial dos grupos. A comparação A/B revela onde a direita cresce sem capturar zonas populares por inteiro.',
    overlay:
      'Sobreposição por donuts mostra zonas de batalha: Centro-Oeste e Sul têm divisão interna suficiente para mudança tática de campo.',
  };
  return textByMode[state.currentMode];
}

/**
 * Atualiza o texto de insight (#u-comp-insight-text). Espelha _updateInsight:
 * o elemento NAO existe na marcacao (unified.html 573-666 nao o contem), entao o
 * early-return mantem isto como no-op fiel — preservado para paridade comportamental.
 */
export function updateComparadorInsight(state: ComparadorModeState): void {
  const insightEl = document.getElementById('u-comp-insight-text');
  if (!insightEl) return;
  insightEl.innerHTML = comparadorInsightHtml(state);
}

/** Limpa a funcao de estilo de zona do mapa (replica deactivate 1274-1277). */
export function deactivateComparador(state: ComparadorModeState, mapCtrl: MapController): void {
  removeComparadorDonuts(state, mapCtrl);
  (mapCtrl as MapController & ComparadorMapRuntimeExtras).currentZoneStyleFn = null;
}
